import zhBidding from '@/i18n/modules/zh-CN/bidding'
import enBidding from '@/i18n/modules/en-US/bidding'

interface BiddingFeeDisplayItem {
  id?: string | number
  feeNameCn?: string
  feeNameEn?: string
  quoteVariant?: string
  feeCategory?: string
  transportSegment?: 'ORIGIN' | 'BASIC' | 'DESTINATION'
  basicFee?: boolean
  sortOrder?: number
}

const FEE_VARIANT_LABELS: Record<string, { zh: string; en: string }> = {
  MIN: { zh: '最低', en: 'MIN' },
  MAX: { zh: '最高', en: 'MAX' },
  AIR_M: { zh: 'M', en: 'M' },
  AIR_N: { zh: 'N', en: 'N' },
  AIR_45_PLUS: { zh: '45+', en: '45+' },
  AIR_100_PLUS: { zh: '100+', en: '100+' },
  AIR_300_PLUS: { zh: '300+', en: '300+' },
  AIR_500_PLUS: { zh: '500+', en: '500+' },
  AIR_700_PLUS: { zh: '700+', en: '700+' },
  AIR_1000_PLUS: { zh: '1000+', en: '1000+' },
  AIR_2000_PLUS: { zh: '2000+', en: '2000+' }
}

/** 根据语言返回报价档位后缀，基础档位不显示后缀。 */
export function getBiddingFeeVariantLabel(quoteVariant?: string, english = false): string {
  const normalized = quoteVariant?.trim().toUpperCase()
  if (!normalized || normalized === 'BASE') return ''
  const labels = FEE_VARIANT_LABELS[normalized]
  return labels ? (english ? labels.en : labels.zh) : normalized
}

/** 组装费用名称及报价档位，全程费用追加国际化运输段，避免导出后同名列无法匹配。 */
export function getBiddingFeeDisplayName(
  fee: BiddingFeeDisplayItem | undefined,
  locale: string,
  fallback = '-'
): string {
  const english = locale.toLowerCase().startsWith('en')
  let baseName = english
    ? (fee?.feeNameEn || fee?.feeNameCn || fallback)
    : (fee?.feeNameCn || fee?.feeNameEn || fallback)
  if (fee?.transportSegment) {
    const group = fee.transportSegment.toLowerCase() as 'origin' | 'basic' | 'destination'
    const messages = english ? enBidding : zhBidding
    baseName += ` · ${messages.tenderDetail.feeSettings.groups[group]}`
  }
  const variantLabel = getBiddingFeeVariantLabel(fee?.quoteVariant, english)
  return variantLabel ? `${baseName} · ${variantLabel}` : baseName
}

type BiddingFeeDisplayGroup = 'ORIGIN' | 'BASIC' | 'DESTINATION'

/**
 * 将费用基础资料归入页面统一的起运港、干线、目的港三段顺序。
 * 基本费用标记优先于费用类，避免海运拼箱基本费被插入目的港费用。
 */
export function getBiddingFeeDisplayGroup(fee: BiddingFeeDisplayItem): BiddingFeeDisplayGroup {
  const category = String(fee.feeCategory || '').trim().toUpperCase()
  if (['港前', '起运港', '起运地', 'ORIGIN', 'PORT_FRONT', 'PREPORT', 'PRE_PORT'].includes(category)) {
    return 'ORIGIN'
  }
  if (['尾程', '目的港', '目的地', 'DESTINATION', 'TAIL', 'LASTMILE', 'LAST_MILE'].includes(category)) {
    return 'DESTINATION'
  }
  return 'BASIC'
}

/**
 * 比较费用展示顺序，先按费用段排序，再保留招标费用配置的段内顺序。
 */
export function compareBiddingFeeDisplayOrder(
  left: BiddingFeeDisplayItem,
  right: BiddingFeeDisplayItem
): number {
  const groupOrder: Record<BiddingFeeDisplayGroup, number> = {
    ORIGIN: 0,
    BASIC: 1,
    DESTINATION: 2
  }
  const groupDifference = groupOrder[getBiddingFeeDisplayGroup(left)]
    - groupOrder[getBiddingFeeDisplayGroup(right)]
  if (groupDifference !== 0) return groupDifference

  const sortDifference = Number(left.sortOrder ?? Number.MAX_SAFE_INTEGER)
    - Number(right.sortOrder ?? Number.MAX_SAFE_INTEGER)
  if (sortDifference !== 0) return sortDifference
  return String(left.id ?? '').localeCompare(String(right.id ?? ''), undefined, { numeric: true })
}

/** 返回按费用段及配置顺序排列的新数组，不修改接口返回的原数组。 */
export function sortBiddingFeeItems<T extends BiddingFeeDisplayItem>(items: T[]): T[] {
  return [...items].sort(compareBiddingFeeDisplayOrder)
}
